import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Divider,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Paper,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BugReportIcon from '@mui/icons-material/BugReport';
import axios from 'axios';
import ExportDebugData from '../sr89Debug/ExportDebugData';

interface BetaRestoDebugFormData {
  calculationId: string;
}

interface BetaRestoDebugResults {
  calculationId: number;
  isotope: string;
  method: string;
  
  // Datos de entrada
  inputData: {
    cpm_muestra: number;
    cpm_fondo: number;
    tiempo_contaje_muestra: number;
    tiempo_contaje_fondo: number;
    cantidad_muestra: number;
    mg_torta: number;
  };
  
  // Eficiencia
  eficiencia: number;
  eficienciaComponents: any;
  
  // CPM Neto
  cpmNeto: number;
  cpmNetoComponents: any;
  
  // Actividad
  activity: number;
  activityComponents: any;
  
  // Componentes de incertidumbre
  uncertaintyComponents: any;
  incSquaredCountComponents: any;
  squaredInverseEfficiencyComponents: any;
  incSquaredSampleQuantityComponents: any;
  
  // Límite Crítico
  limiteCritico: number;
  limiteCriticoComponents: any;
  
  // LID
  lid: number;
  lidComponents: any;
  
  // Incertidumbre
  uncertainty: number;
  uncertaintyDetailedComponents: any;
  
  // Datos de calibración
  calibrationData: any;
  primaryPatternData: any;
  secondaryPatternData: any;
  fcData: any;
  relativeUncertaintyData: any;
  
  // Resultados finales
  finalResults: any;
  
  // CalibrationUncertainty
  calibrationUncertaintyData: any;
}

const BetaRestoDebugPage: React.FC = () => {
  const navigate = useNavigate();
  const [results, setResults] = useState<BetaRestoDebugResults | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [calculationId, setCalculationId] = useState<string>('');

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!calculationId) {
      setError('Por favor ingrese un ID de cálculo válido');
      return;
    }
    
    setLoading(true);
    setError(null);
    setResults(null);
    
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`http://localhost:8000/api/debug/beta-resto/${calculationId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setResults(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al obtener los datos de depuración');
    } finally {
      setLoading(false);
    }
  };

  const renderSection = (title: string, data: Array<{ label: string; value: any; formula?: string; components?: any }>) => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom color="primary">
          {title}
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          {data.map((item, index) => (
            <Grid item xs={12} key={index}>
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" fontWeight="bold">
                  {item.label}
                </Typography>
                {item.formula && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', display: 'block', mb: 0.5 }}>
                    {item.formula}
                  </Typography>
                )}
                <Typography variant="h6" fontWeight="bold" color="primary" sx={{ my: 1 }}>
                  {typeof item.value === 'number' ? item.value.toExponential(6) : item.value}
                </Typography>
                
                {/* Mostrar componentes si existen */}
                {item.components && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'grey.300' }}>
                    <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
                      📊 Componentes del cálculo:
                    </Typography>
                    <Grid container spacing={1}>
                      {Object.entries(item.components).map(([key, value]: [string, any]) => (
                        <Grid item xs={12} sm={6} md={4} key={key}>
                          <Box sx={{ p: 1, bgcolor: 'grey.50', borderRadius: 0.5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                              {key.replace(/_/g, ' ')}
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.85rem', fontFamily: 'monospace' }}>
                              {typeof value === 'number' ? value.toExponential(4) : value}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}
              </Box>
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Paper elevation={2} sx={{ p: 2, mb: 3, bgcolor: 'info.main', color: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <BugReportIcon sx={{ fontSize: 40 }} />
            <Box>
              <Typography variant="h4" fontWeight="bold">
                Debug Beta Resto
              </Typography>
              <Typography variant="subtitle1">
                Análisis ultra-detallado de cálculos de actividad, incertidumbre y LID
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/admin/debug')}
            sx={{ bgcolor: 'white', color: 'info.main', '&:hover': { bgcolor: 'grey.100' } }}
          >
            Volver
          </Button>
        </Box>
      </Paper>

      {/* Formulario de búsqueda */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <form onSubmit={onSubmit}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  label="ID del Cálculo"
                  type="number"
                  required
                  value={calculationId}
                  onChange={(e) => setCalculationId(e.target.value)}
                  helperText="Ingrese el ID del cálculo que desea analizar"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Button
                  type="submit"
                  variant="contained"
                  color="info"
                  fullWidth
                  size="large"
                  disabled={loading || !calculationId}
                  startIcon={loading ? <CircularProgress size={20} /> : <BugReportIcon />}
                >
                  {loading ? 'Analizando...' : 'Analizar'}
                </Button>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Resultados */}
      {results && (
        <>
          {/* Información del cálculo */}
          <Card sx={{ mb: 2, bgcolor: 'info.light' }}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">ID de Cálculo</Typography>
                  <Typography variant="h6" fontWeight="bold">{results.calculationId}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">Isótopo</Typography>
                  <Typography variant="h6" fontWeight="bold">{results.isotope}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">Método</Typography>
                  <Typography variant="h6" fontWeight="bold">{results.method}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Botones de exportación */}
          <ExportDebugData results={results as any} calculationId={calculationId} />

          {/* Sección 1: Datos de Entrada */}
          {renderSection('1. Datos de Entrada', [
            { label: 'CPM Muestra', value: results.inputData.cpm_muestra },
            { label: 'CPM Fondo', value: results.inputData.cpm_fondo },
            { label: 'Tiempo Contaje Muestra (min)', value: results.inputData.tiempo_contaje_muestra },
            { label: 'Tiempo Contaje Fondo (min)', value: results.inputData.tiempo_contaje_fondo },
            { label: 'Cantidad Muestra', value: results.inputData.cantidad_muestra },
            { label: 'mg Torta (SQP)', value: results.inputData.mg_torta },
          ])}

          {/* Sección 2: Eficiencia */}
          {renderSection('2. Eficiencia de Detección', [
            { 
              label: 'Eficiencia', 
              value: results.eficiencia,
              formula: results.eficienciaComponents?.formula,
              components: results.eficienciaComponents
            },
          ])}

          {/* Sección 3: CPM Neto */}
          {renderSection('3. CPM Neto', [
            { 
              label: 'CPM Neto', 
              value: results.cpmNeto,
              formula: results.cpmNetoComponents?.formula,
              components: results.cpmNetoComponents
            },
          ])}

          {/* Sección 4: Actividad */}
          {renderSection('4. Cálculo de Actividad', [
            { 
              label: 'Actividad (Bq)', 
              value: results.activity,
              formula: results.activityComponents?.formula,
              components: results.activityComponents
            },
          ])}

          {/* Sección 5: Inc² Contaje */}
          {results.incSquaredCountComponents && renderSection('5. Inc² Contaje', [
            { 
              label: 'Inc² Contaje', 
              value: results.uncertaintyComponents?.inc_cuadrado_contaje,
              formula: results.incSquaredCountComponents?.formula,
              components: results.incSquaredCountComponents
            },
          ])}

          {/* Sección 6: Inc² Inversa Eficiencia */}
          {results.squaredInverseEfficiencyComponents && renderSection('6. Inc² Inversa de la Eficiencia', [
            { 
              label: 'Inc² Inversa Eficiencia', 
              value: results.uncertaintyComponents?.inc_cuadrado_inversa_eficiencia,
              formula: results.squaredInverseEfficiencyComponents?.formula,
              components: results.squaredInverseEfficiencyComponents
            },
          ])}

          {/* Sección 7: Inc² Cantidad Muestra */}
          {results.incSquaredSampleQuantityComponents && renderSection('7. Inc² Cantidad de Muestra', [
            { 
              label: 'Inc² Cantidad Muestra', 
              value: results.uncertaintyComponents?.inc_cuadrado_cantidad_muestra,
              formula: results.incSquaredSampleQuantityComponents?.formula,
              components: results.incSquaredSampleQuantityComponents
            },
          ])}

          {/* Sección 8: Límite Crítico */}
          {renderSection('8. Límite Crítico', [
            { 
              label: 'Límite Crítico', 
              value: results.limiteCritico,
              formula: results.limiteCriticoComponents?.formula,
              components: results.limiteCriticoComponents
            },
          ])}

          {/* Sección 9: LID */}
          {renderSection('9. Límite de Detección (LID)', [
            { 
              label: 'LID (Bq)', 
              value: results.lid,
              formula: results.lidComponents?.formula,
              components: results.lidComponents
            },
          ])}

          {/* Sección 10: Incertidumbre */}
          {renderSection('10. Incertidumbre', [
            { 
              label: 'Incertidumbre (Bq)', 
              value: results.uncertainty,
              formula: results.uncertaintyDetailedComponents?.formula,
              components: results.uncertaintyDetailedComponents
            },
          ])}

          {/* Sección 11: Datos de Calibración */}
          {results.calibrationData && renderSection('11. Datos de Calibración', [
            { label: 'Actividad Patrón a t=0 (ta0)', value: results.calibrationData.ta0 },
            { label: 'Inc. Actividad Patrón (tua0)', value: results.calibrationData.tua0 },
            { label: 'Masa Patrón Primario (tmpp)', value: results.calibrationData.tmpp },
            { label: 'Masa Patrón Secundario (tmps)', value: results.calibrationData.tmps },
            { label: 'Masa FC Mayor Cantidad (tmpsfcal)', value: results.calibrationData.tmpsfcal },
            { label: 'CPM FC Mayor Cantidad (tcpmps)', value: results.calibrationData.tcpmps },
            { label: 'Tiempo Calibración (tcal)', value: results.calibrationData.tcal },
            { label: 'DPM/g FC (tdpmg)', value: results.calibrationData.tdpmg },
            { label: 'Factor Autoabsorción (tfact_aa)', value: results.calibrationData.tfact_aa },
          ])}

          {/* Sección 12: Datos Patrón Primario */}
          {results.primaryPatternData && renderSection('12. Datos del Patrón Primario', [
            { label: 'Actividad Patrón Primario', value: results.primaryPatternData.actividad_patron_primario },
            { label: 'Incertidumbre Patrón Primario', value: results.primaryPatternData.incertidumbre_patron_primario },
            { label: 'Masa Patrón Primario (mpp)', value: results.primaryPatternData.masa_patron_primario_mpp },
            { label: 'Incertidumbre Masa Patrón', value: results.primaryPatternData.incertidumbre_masa_patron },
          ])}

          {/* Sección 13: Datos Patrón Secundario */}
          {results.secondaryPatternData && renderSection('13. Datos del Patrón Secundario', [
            { label: 'Masa Patrón Secundario', value: results.secondaryPatternData.masa_patron_secundario },
            { label: 'Incertidumbre Masa Patrón', value: results.secondaryPatternData.incertidumbre_masa_patron },
            { label: 'Masa Mayor Cantidad Centelleador', value: results.secondaryPatternData.masa_mayor_cantidad_centelleador },
            { label: 'Inc. Masa Mayor Cantidad', value: results.secondaryPatternData.inc_masa_mayor_cantidad },
          ])}

          {/* Sección 14: Datos de Fuente de Calibración (FC) */}
          {results.fcData && renderSection('14. Datos de Fuente de Calibración (FC)', [
            { label: 'CPM FC Mayor Cantidad', value: results.fcData.cpm_fc_mayor_cantidad },
            { label: 'Actividad FC en Fecha Contaje', value: results.fcData.actividad_fc_fecha_contaje },
            { label: 'Inc² Actividad FC', value: results.fcData.inc_cuadrado_actividad_fc },
            { label: 'Inc. Volumen FC Espesor Cero', value: results.fcData.inc_volumen_fc_espesor_cero },
            { label: 'Inc. Contaje² FC', value: results.fcData.inc_contaje_cuadrado_fc },
          ])}

          {/* Sección 15: Incertidumbres Relativas */}
          {results.relativeUncertaintyData && renderSection('15. Incertidumbres Relativas', [
            { label: 'Inc. Rel.² Factor Autoabsorción', value: results.relativeUncertaintyData.inc_relativa_cuadrado_factor_autoabsorcion },
            { label: 'Inc.² Inversa Eficiencia Muestra', value: results.relativeUncertaintyData.inc_cuadrado_inversa_eficiencia_muestra },
          ])}

          {/* Sección 16: Resultados Finales Comparativos */}
          {results.finalResults && renderSection('16. Resultados Finales Comparativos', [
            { label: 'Actividad (Calculada)', value: results.finalResults.actividad_calculada },
            { label: 'Actividad (from Results)', value: results.finalResults.actividad_from_results },
            { label: 'Incertidumbre (Calculada)', value: results.finalResults.incertidumbre_calculada },
            { label: 'Incertidumbre (from Results)', value: results.finalResults.incertidumbre_from_results },
            { label: 'LID (Calculado)', value: results.finalResults.lid_calculado },
            { label: 'LID (from Results)', value: results.finalResults.lid_from_results },
            { label: 'Límite Crítico (Calculado)', value: results.finalResults.limite_critico_calculado },
            { label: 'Límite Crítico (from Results)', value: results.finalResults.limite_critico_from_results },
          ])}

          {/* Sección 17: Datos de CalibrationUncertainty */}
          {results.calibrationUncertaintyData && !results.calibrationUncertaintyData.error && (
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  17. Datos de CalibrationUncertainty
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                {/* Información general */}
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, mt: 2 }}>
                  📋 Información General
                </Typography>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">ID</Typography>
                      <Typography variant="body1" fontWeight="bold">{results.calibrationUncertaintyData.id}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">Fecha</Typography>
                      <Typography variant="body1" fontWeight="bold">{results.calibrationUncertaintyData.date}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">Tipo Incertidumbre</Typography>
                      <Typography variant="body1" fontWeight="bold">{results.calibrationUncertaintyData.tipo_incertidumbre}</Typography>
                    </Box>
                  </Grid>
                </Grid>

                {/* Incertidumbres de masa de patrones */}
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, mt: 2 }}>
                  🎯 Incertidumbres de Masa de Patrones
                </Typography>
                <Grid container spacing={1} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">EUMPP</Typography>
                      <Typography variant="body2" fontFamily="monospace">{results.calibrationUncertaintyData.eumpp?.toExponential(4) || 'N/A'}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">EUMPS</Typography>
                      <Typography variant="body2" fontFamily="monospace">{results.calibrationUncertaintyData.eumps?.toExponential(4) || 'N/A'}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">Inc. Masa Patrón Terciario</Typography>
                      <Typography variant="body2" fontFamily="monospace">{results.calibrationUncertaintyData.mass_uncertainty_tertiary_pattern?.toExponential(4) || 'N/A'}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">Inc. Fuente Pat. Terc.</Typography>
                      <Typography variant="body2" fontFamily="monospace">{results.calibrationUncertaintyData.uncertainty_source_pat_terc?.toExponential(4) || 'N/A'}</Typography>
                    </Box>
                  </Grid>
                </Grid>

                {/* Comentarios */}
                {results.calibrationUncertaintyData.internal_comments && (
                  <>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, mt: 2 }}>
                      💬 Comentarios Internos
                    </Typography>
                    <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                      <Typography variant="body2">{results.calibrationUncertaintyData.internal_comments}</Typography>
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* Mostrar error si no se encontró CalibrationUncertainty */}
          {results.calibrationUncertaintyData?.error && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight="bold">
                {results.calibrationUncertaintyData.error}
              </Typography>
              {results.calibrationUncertaintyData.message && (
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                  {results.calibrationUncertaintyData.message}
                </Typography>
              )}
            </Alert>
          )}
        </>
      )}
    </Box>
  );
};

export default BetaRestoDebugPage;
